import { useEffect, useMemo, useRef } from 'react'
import { dia } from '@joint/core'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import type { ParsedEntity } from '../../../map-editor-features/csv/lib/csvParser'

interface UseEntityMappingRestoreProps {
  currentFloor: string | null
  parsedEntities: ParsedEntity[] | undefined
  /**
   * Optional graph reference for the dashboard re-entry path where
   * `parsedEntities` is undefined (CSV not re-parsed). When provided, the hook
   * extracts `{entityHandle, layer}` from `graph.getElements()` as a fallback
   * data source so layer→type mapping can still be replayed.
   */
  graph?: dia.Graph | null
}

/**
 * Pure helper: extract entity-handle / layer pairs from graph elements.
 *
 * `applyLayerTypeMappingsToEntities` only reads `entityHandle` and `layer`
 * from each ParsedEntity (mappingActions.ts:203,209), so the placeholder
 * `entityType`/`points` fields are intentional. If that contract changes,
 * this helper must be revisited.
 */
export function extractEntitiesFromGraph(graph: dia.Graph): ParsedEntity[] {
  const out: ParsedEntity[] = []
  for (const el of graph.getElements()) {
    const data = el.get('data') as { entityHandle?: string; layer?: string } | undefined
    if (!data?.entityHandle || !data.layer) continue
    out.push({
      entityHandle: data.entityHandle,
      layer: data.layer,
      entityType: 'POINT',
      points: [],
    })
  }
  return out
}

/**
 * Reload 시 layer-level mapping(`layerTypeMappings`)을 entity-level mapping
 * (`mappings`)으로 다시 펼치는 effect 훅.
 *
 * 배경: `loadMappings`는 `layerTypeMappings`만 API에서 채우고 끝나며,
 * `mappings`는 `LayerMappingModal.addMapping()` 호출 시에만 갱신되는 로컬 전용
 * 스토어다. PropertyEditor는 `mappings`를 조회하므로 reload 후엔 모든 객체가
 * "No type assigned"로 표시되는 문제가 있었다. 본 훅이 두 스토어를 React
 * 레이어에서 다시 연결한다.
 *
 * 두 가지 데이터 소스를 지원:
 *   1) `parsedEntities` — CSV 임포트 직후 useCSVStore.parsedData 경로 (우선)
 *   2) `graph` — dashboard 재진입처럼 parsedData가 비어있는 reload 경로 (fallback).
 *      graph.fromJSON()으로 복원된 elements의 cell.data에서 entityHandle/layer 추출.
 */
export function useEntityMappingRestore({
  currentFloor,
  parsedEntities,
  graph,
}: UseEntityMappingRestoreProps): void {
  const layerTypeMappings = useObjectTypeStore(s => s.layerTypeMappings)
  const mappingsLoading = useObjectTypeStore(s => s.mappingsLoading)
  const apply = useObjectTypeStore(s => s.applyLayerTypeMappingsToEntities)

  // 같은 (floor, source, mappings count, entities count) 조합에 대한 재호출 차단.
  // `source` 포함 이유: CSV 직후 흐름과 graph reload 흐름이 동일 size일 때 충돌 방지.
  const lastAppliedKeyRef = useRef<string | null>(null)

  // graph 기반 entities 캐시. layerTypeMappings 변경 시점(`loadMappings` 완료)에
  // 재추출 트리거되도록 deps에 포함. graph 자체는 안정 참조이므로 매 렌더 호출은
  // 발생하지 않는다.
  const graphEntities = useMemo<ParsedEntity[] | undefined>(() => {
    if (!graph) return undefined
    if (graph.getElements().length === 0) return undefined
    return extractEntitiesFromGraph(graph)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layerTypeMappings is
    // an intentional re-extract trigger (graph contents may have arrived after
    // mappings did, or vice versa).
  }, [graph, layerTypeMappings])

  // parsedEntities가 있으면 우선, 없으면 graph fallback.
  const effectiveEntities = parsedEntities ?? graphEntities

  useEffect(() => {
    if (!currentFloor) return
    if (mappingsLoading) return
    if (!effectiveEntities || effectiveEntities.length === 0) return

    const floorMappings = layerTypeMappings.filter(
      m => String(m.floorId) === String(currentFloor)
    )
    if (floorMappings.length === 0) return

    const source = parsedEntities ? 'csv' : 'graph'
    const key = `${currentFloor}:${source}:${floorMappings.length}:${effectiveEntities.length}`
    if (lastAppliedKeyRef.current === key) return

    apply(currentFloor, effectiveEntities)
    lastAppliedKeyRef.current = key
  }, [
    currentFloor,
    layerTypeMappings,
    effectiveEntities,
    parsedEntities,
    mappingsLoading,
    apply,
  ])
}
